import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { 
  Users, 
  Target, 
  Star,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Settings,
  Zap,
  Crown,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Award,
  Lightbulb,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Eye,
  Edit,
  Download,
  RefreshCw,
  Filter,
  Search,
  UserCheck,
  Brain,
  Rocket,
  Diamond,
  Gem,
  Sparkles,
  FlameKindling,
  Calendar,
  FileText,
  Plus,
  User,
  ThumbsUp
} from 'lucide-react';

// Lazy load dos componentes pesados
const SucessaoTab = lazy(() => import('./SucessaoTab'));
const CalibracaoTab = lazy(() => import('./CalibracaoTab'));

const API = process.env.REACT_APP_BACKEND_URL;

const NineBoxInteligente = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('colaboradores');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [matrizes, setMatrizes] = useState([]);
  const [matrizAtiva, setMatrizAtiva] = useState(null);
  const [posicoes, setPosicoes] = useState([]);
  const [visualizacao, setVisualizacao] = useState({});
  const [colaboradores, setColaboradores] = useState([]);
  const [insightsIA, setInsightsIA] = useState({});
  const [criterios, setCriterios] = useState([]);
  
  // Form states
  const [novaMatriz, setNovaMatriz] = useState({
    nome: '',
    descricao: '',
    periodo_inicio: '',
    periodo_fim: '',
    peso_performance: 60,
    peso_potencial: 40,
    fonte_performance: 'avaliacao_desempenho',
    fonte_potencial: 'avaliacao_desempenho',
    permitir_ajuste_manual: true,
    requer_calibracao: true,
    gerar_pdi_automatico: true
  });
  
  const [filtros, setFiltros] = useState({
    departamento: '',
    cargo: '',
    tempo_empresa: '',
    quadrante: ''
  });

  const [selectedColaborador, setSelectedColaborador] = useState(null);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    total_colaboradores: 0,
    distribuicao_quadrantes: {},
    alto_potencial: 0,
    alta_performance: 0,
    talentos_criticos: 0,
    necessita_atencao: 0
  });
  
  // Sidebar states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedQuadrante, setSelectedQuadrante] = useState(null);
  
  // Colaboradores elegíveis states
  const [colaboradoresElegiveis, setColaboradoresElegiveis] = useState([]);
  const [colaboradoresSelecionados, setColaboradoresSelecionados] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [mostrarApenasComAvaliacao, setMostrarApenasComAvaliacao] = useState(false);

  // Configurações dos quadrantes
  const quadrantesConfig = {
    1: { nome: 'Desafio de Performance', cor: 'bg-red-100 border-red-300', icon: AlertTriangle, estrategia: 'Plano corretivo imediato' },
    2: { nome: 'Talento Emergente', cor: 'bg-yellow-100 border-yellow-300', icon: Clock, estrategia: 'Coaching intensivo' },
    3: { nome: 'Alto Potencial', cor: 'bg-blue-100 border-blue-300', icon: Rocket, estrategia: 'Preparar para sucessão' },
    4: { nome: 'Performance Questionável', cor: 'bg-orange-100 border-orange-300', icon: Shield, estrategia: 'Treinamentos técnicos' },
    5: { nome: 'Talento Estável', cor: 'bg-gray-100 border-gray-300', icon: Users, estrategia: 'Manter estabilidade' },
    6: { nome: 'Futuro Líder', cor: 'bg-purple-100 border-purple-300', icon: Crown, estrategia: 'Job rotation + liderança' },
    7: { nome: 'Performance Baixa', cor: 'bg-red-200 border-red-400', icon: TrendingDown, estrategia: 'Reavaliar função' },
    8: { nome: 'Especialista Sólido', cor: 'bg-green-100 border-green-300', icon: Award, estrategia: 'Recompensa por entrega' },
    9: { nome: 'Talento Estrela', cor: 'bg-emerald-100 border-emerald-300', icon: Star, estrategia: 'Retenção e reconhecimento' }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (currentTab === 'colaboradores' && matrizAtiva) {
      loadColaboradoresElegiveis();
    }
  }, [currentTab, matrizAtiva]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadMatrizes(),
        loadColaboradores(),
        loadCriterios()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados iniciais');
    } finally {
      setLoading(false);
    }
  };

  const loadMatrizes = async () => {
    try {
      const response = await axios.get(`${API}/api/9box/matriz/listar`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        const matrizesData = response.data.matrizes || [];
        setMatrizes(matrizesData);
        
        // Selecionar matriz ativa
        const ativa = matrizesData.find(m => m.status === 'ativo');
        if (ativa) {
          setMatrizAtiva(ativa);
          await loadVisualizacaoMatriz(ativa.id);
        } else if (matrizesData.length > 0) {
          // Se não há matriz ativa, pega a mais recente
          setMatrizAtiva(matrizesData[0]);
          await loadVisualizacaoMatriz(matrizesData[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar matrizes:', error);
      toast.error('Erro ao carregar matrizes');
    }
  };

  const loadColaboradores = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setColaboradores(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  };

  const loadCriterios = async () => {
    try {
      // Simulação de critérios
      const mockCriterios = [
        { id: '1', nome: 'Resultados de Metas', tipo: 'performance', peso: 1.0 },
        { id: '2', nome: 'Qualidade de Entrega', tipo: 'performance', peso: 0.8 },
        { id: '3', nome: 'Liderança', tipo: 'potencial', peso: 1.0 },
        { id: '4', nome: 'Inovação', tipo: 'potencial', peso: 0.9 },
        { id: '5', nome: 'Aprendizagem Contínua', tipo: 'potencial', peso: 0.7 }
      ];
      setCriterios(mockCriterios);
    } catch (error) {
      console.error('Erro ao carregar critérios:', error);
    }
  };

  const loadColaboradoresElegiveis = async () => {
    if (!matrizAtiva) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/9box/matriz/${matrizAtiva.id}/colaboradores-elegiveis`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      if (response.data.success) {
        const colaboradores = response.data.colaboradores || [];
        setColaboradoresElegiveis(colaboradores);
        
        // Carregar seleções anteriores
        const selecionados = colaboradores
          .filter(c => c.selecionado)
          .map(c => c.id);
        setColaboradoresSelecionados(selecionados);
        
        // Atualizar selectAll
        setSelectAll(selecionados.length === colaboradores.length && colaboradores.length > 0);
      }
    } catch (error) {
      console.error('Erro ao carregar colaboradores elegíveis:', error);
      toast.error('Erro ao carregar lista de colaboradores');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleColaborador = (colaboradorId) => {
    setColaboradoresSelecionados(prev => {
      if (prev.includes(colaboradorId)) {
        return prev.filter(id => id !== colaboradorId);
      } else {
        return [...prev, colaboradorId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setColaboradoresSelecionados([]);
    } else {
      setColaboradoresSelecionados(colaboradoresElegiveis.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSalvarSelecao = async () => {
    if (!matrizAtiva) {
      toast.error('Selecione uma matriz primeiro');
      return;
    }

    try {
      setLoading(true);
      
      await axios.post(
        `${API}/api/9box/matriz/${matrizAtiva.id}/colaboradores-selecionados`,
        {
          colaboradores_ids: colaboradoresSelecionados
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success(`Seleção de ${colaboradoresSelecionados.length} colaboradores salva!`);
      await loadColaboradoresElegiveis(); // Recarregar para atualizar status
    } catch (error) {
      console.error('Erro ao salvar seleção:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar seleção');
    } finally {
      setLoading(false);
    }
  };

  const loadVisualizacaoMatriz = async (matrizId) => {
    if (!matrizId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/9box/matriz/${matrizId}/visualizacao`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      if (response.data) {
        setVisualizacao(response.data);
        
        // Atualizar métricas do dashboard
        setDashboardMetrics({
          total_colaboradores: response.data.total_colaboradores || 0,
          distribuicao_quadrantes: response.data.distribuicao_quadrantes || {},
          alto_potencial: response.data.estatisticas?.alto_potencial || 0,
          alta_performance: response.data.estatisticas?.alta_performance || 0,
          talentos_criticos: response.data.estatisticas?.talentos_criticos || 0,
          necessita_atencao: response.data.estatisticas?.necessita_atencao || 0
        });
      }
    } catch (error) {
      console.error('Erro ao carregar visualização:', error);
      if (error.response?.status === 404 || error.response?.data?.message?.includes('Nenhuma posição')) {
        toast.info('Nenhuma posição calculada ainda. Clique em "Calcular Posições" para processar.');
        setVisualizacao({
          matriz_id: matrizId,
          total_colaboradores: 0,
          distribuicao_quadrantes: {},
          quadrantes: {},
          insights_gerais: null,
          estatisticas: {
            alto_potencial: 0,
            alta_performance: 0,
            talentos_criticos: 0,
            necessita_atencao: 0
          }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMatriz = async (e) => {
    e.preventDefault();
    try {
      if (!novaMatriz.periodo_inicio || !novaMatriz.periodo_fim) {
        toast.error('Preencha as datas de início e fim');
        return;
      }

      if (novaMatriz.peso_performance === '' || novaMatriz.peso_potencial === '') {
        toast.error('Preencha os pesos da avaliação');
        return;
      }

      const dataInicio = new Date(novaMatriz.periodo_inicio);
      const dataFim = new Date(novaMatriz.periodo_fim);

      if (isNaN(dataInicio.getTime()) || isNaN(dataFim.getTime())) {
        toast.error('Datas inválidas');
        return;
      }

      if (dataFim <= dataInicio) {
        toast.error('Data de término deve ser posterior à data de início');
        return;
      }

      setLoading(true);
      
      const matrizData = {
        ...novaMatriz,
        peso_performance: Number(novaMatriz.peso_performance),
        peso_potencial: Number(novaMatriz.peso_potencial),
        periodo_inicio: dataInicio.toISOString(),
        periodo_fim: dataFim.toISOString()
      };

      await axios.post(`${API}/api/9box/matriz/criar`, matrizData);
      
      toast.success('Matriz criada com sucesso!');
      setNovaMatriz({
        nome: '',
        descricao: '',
        periodo_inicio: '',
        periodo_fim: '',
        peso_performance: 60,
        peso_potencial: 40,
        fonte_performance: 'avaliacao_desempenho',
        fonte_potencial: 'avaliacao_desempenho',
        permitir_ajuste_manual: true,
        requer_calibracao: true,
        gerar_pdi_automatico: true
      });
      
      await loadMatrizes();
      
      // Redirecionar para a tela de gerenciar matrizes
      setTimeout(() => {
        navigate('/9box/configuracao-ciclos');
      }, 1500);
    } catch (error) {
      console.error('Erro ao criar matriz:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar matriz');
    } finally {
      setLoading(false);
    }
  };

  const handleCalcularPosicoes = async () => {
    if (!matrizAtiva) {
      toast.error('Selecione uma matriz primeiro');
      return;
    }
    
    if (colaboradoresSelecionados.length === 0) {
      toast.error('Selecione pelo menos um colaborador para calcular posições');
      return;
    }

    try {
      setLoading(true);
      
      // Primeiro, salvar a seleção de colaboradores
      await axios.post(
        `${API}/api/9box/matriz/${matrizAtiva.id}/colaboradores-selecionados`,
        {
          colaboradores_ids: colaboradoresSelecionados
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Depois, calcular as posições
      const response = await axios.post(`${API}/api/9box/calcular-posicoes`, {
        matriz_id: matrizAtiva.id
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = response.data;
      
      // Verificar se houve colaboradores pulados
      if (data.colaboradores_pulados > 0) {
        toast.warning(
          `⚠️ ${data.colaboradores_processados} de ${data.colaboradores_selecionados} colaboradores processados. ` +
          `${data.colaboradores_pulados} foram pulados por falta de avaliações válidas.`,
          { duration: 6000 }
        );
      } else {
        toast.success(`✅ Posições calculadas para ${data.colaboradores_processados} colaboradores!`);
      }
      
      // Recarregar a visualização da matriz para atualizar o dashboard
      await loadVisualizacaoMatriz(matrizAtiva.id);
      
      // Mudar para aba dashboard
      setCurrentTab('dashboard');
    } catch (error) {
      console.error('Erro ao calcular posições:', error);
      toast.error(error.response?.data?.detail || 'Erro ao calcular posições automaticamente');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarPlanoSucessao = async (colaboradorId) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/api/9box/${colaboradorId}/plano-sucessao`);
      
      toast.success('Plano de sucessão gerado com sucesso!');
      
      // Aqui você poderia abrir um modal ou navegar para uma página com os detalhes
      console.log('Plano de sucessão:', response.data);
    } catch (error) {
      console.error('Erro ao gerar plano de sucessão:', error);
      toast.error('Erro ao gerar plano de sucessão');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleQuadranteClick = (quadrante, colaboradores) => {
    setSelectedQuadrante({ numero: quadrante, colaboradores });
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setSelectedQuadrante(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      rascunho: { color: 'bg-gray-100 text-gray-800', label: 'Rascunho' },
      ativo: { color: 'bg-green-100 text-green-800', label: 'Ativo' },
      arquivado: { color: 'bg-blue-100 text-blue-800', label: 'Arquivado' }
    };
    
    const config = statusConfig[status] || statusConfig.rascunho;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const renderColaboradores = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <UserCheck className="h-6 w-6 text-blue-600" />
                <span>Seleção de Colaboradores</span>
              </CardTitle>
              <CardDescription className="mt-2">
                Escolha quais colaboradores serão incluídos na Matriz 9Box. 
                Apenas colaboradores ativos e com avaliações são elegíveis.
              </CardDescription>
            </div>
            <Button 
              onClick={() => loadColaboradoresElegiveis()}
              variant="outline"
              size="sm"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar Lista
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <Users className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Total Elegíveis</p>
                    <p className="text-2xl font-bold">{colaboradoresElegiveis.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Selecionados</p>
                    <p className="text-2xl font-bold">{colaboradoresSelecionados.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <Target className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-600">Com Avaliações</p>
                    <p className="text-2xl font-bold">
                      {colaboradoresElegiveis.filter(c => c.tem_avaliacao).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtros */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="🔍 Buscar por nome..."
                  value={filtroNome}
                  onChange={(e) => setFiltroNome(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="filtro-avaliacao"
                  checked={mostrarApenasComAvaliacao}
                  onChange={(e) => setMostrarApenasComAvaliacao(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="filtro-avaliacao" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Apenas com Avaliação
                </label>
              </div>
            </div>
          </div>

          {/* Ações em Massa */}
          <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="font-medium">Selecionar Todos</span>
            </div>
            
            <div className="flex space-x-2">
              <Button
                onClick={handleSalvarSelecao}
                variant="outline"
                size="sm"
                disabled={loading || colaboradoresSelecionados.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Salvar Seleção
              </Button>
              
              <Button
                onClick={handleCalcularPosicoes}
                disabled={loading || colaboradoresSelecionados.length === 0}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Recalcular Posições ({colaboradoresSelecionados.length})
              </Button>
            </div>
          </div>

          {/* Lista de Colaboradores */}
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
              <p className="text-gray-500">Carregando colaboradores...</p>
            </div>
          ) : colaboradoresElegiveis.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Nenhum colaborador elegível encontrado</p>
              <p className="text-sm text-gray-400 mt-2">
                Certifique-se de que há colaboradores ativos com avaliações concluídas
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {colaboradoresElegiveis
                .filter(c => {
                  // Filtro por nome
                  const matchNome = !filtroNome || c.nome.toLowerCase().includes(filtroNome.toLowerCase());
                  // Filtro por avaliação
                  const matchAvaliacao = !mostrarApenasComAvaliacao || c.tem_avaliacao;
                  return matchNome && matchAvaliacao;
                })
                .map((colaborador) => {
                const isSelected = colaboradoresSelecionados.includes(colaborador.id);
                
                return (
                  <Card 
                    key={colaborador.id}
                    className={`transition-all cursor-pointer hover:shadow-md ${
                      isSelected ? 'border-blue-500 bg-blue-50' : ''
                    }`}
                    onClick={() => handleToggleColaborador(colaborador.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{colaborador.nome}</p>
                              <div className="flex items-center space-x-4 mt-1">
                                <span className="text-sm text-gray-600">
                                  {colaborador.cargo || 'Cargo não informado'}
                                </span>
                                {colaborador.departamento && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-sm text-gray-600">
                                      {colaborador.departamento}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              {colaborador.tem_avaliacao ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Com Avaliação
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600 border-orange-300">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Sem Avaliação
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informações Adicionais */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">Dica:</p>
              <p className="text-blue-700">
                Após selecionar os colaboradores desejados, clique em <strong>"Recalcular Posições"</strong> 
                para processar a Matriz 9Box. Os resultados aparecerão automaticamente no Dashboard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Métricas Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="text-2xl font-bold">{visualizacao.total_colaboradores || 0}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Total de Colaboradores</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Star className="h-5 w-5 text-yellow-600" />
              <span className="text-2xl font-bold">{visualizacao.estatisticas?.talentos_criticos || 0}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Talentos Críticos</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Rocket className="h-5 w-5 text-purple-600" />
              <span className="text-2xl font-bold">{visualizacao.estatisticas?.alto_potencial || 0}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Alto Potencial</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <span className="text-2xl font-bold">{visualizacao.estatisticas?.necessita_atencao || 0}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Necessita Atenção</p>
          </CardContent>
        </Card>
      </div>

      {/* Matriz Ativa */}
      {matrizAtiva && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <Diamond className="h-5 w-5 text-purple-600" />
                  <span>Matriz Ativa: {matrizAtiva.nome}</span>
                </CardTitle>
                <CardDescription>{matrizAtiva.descricao}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <p className="text-sm font-medium">Período</p>
                <p className="text-sm text-gray-600">
                  {formatDate(matrizAtiva.periodo_inicio)} - {formatDate(matrizAtiva.periodo_fim)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Configuração</p>
                <p className="text-sm text-gray-600">
                  {matrizAtiva.peso_performance}% Performance / {matrizAtiva.peso_potencial}% Potencial
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Status</p>
                {getStatusBadge(matrizAtiva.status)}
              </div>
            </div>

            {/* Insights de IA */}
            {visualizacao.insights_gerais && (
              <Card className="bg-purple-50 border-purple-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center space-x-2 text-lg">
                    <Brain className="h-5 w-5 text-purple-600" />
                    <span>Análise Estratégica de Talentos (IA)</span>
                    <Badge className="bg-purple-100 text-purple-800">
                      {Math.round(visualizacao.insights_gerais.confianca * 100)}% confiança
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h5 className="font-medium mb-2 flex items-center">
                      <Lightbulb className="h-4 w-4 mr-1 text-yellow-600" />
                      Insights Estratégicos
                    </h5>
                    <ul className="space-y-1">
                      {visualizacao.insights_gerais.insights.map((insight, index) => (
                        <li key={index} className="text-sm text-gray-700 flex items-start">
                          <span className="inline-block w-1 h-1 bg-purple-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h5 className="font-medium mb-2 flex items-center">
                      <ArrowRight className="h-4 w-4 mr-1 text-blue-600" />
                      Recomendações Estratégicas
                    </h5>
                    <ul className="space-y-1">
                      {visualizacao.insights_gerais.recomendacoes.map((rec, index) => (
                        <li key={index} className="text-sm text-gray-700 flex items-start">
                          <span className="inline-block w-1 h-1 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-medium mb-2 flex items-center">
                      <Target className="h-4 w-4 mr-1 text-green-600" />
                      Prioridades de Ação
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {visualizacao.insights_gerais.prioridades.map((prioridade, index) => (
                        <Badge key={index} className="bg-green-100 text-green-800">
                          {prioridade}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}

      {/* Distribuição por Quadrantes */}
      {visualizacao.distribuicao_quadrantes && (
        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Quadrantes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((quadrante) => {
                const config = quadrantesConfig[quadrante];
                const IconComponent = config.icon;
                const count = visualizacao.distribuicao_quadrantes[quadrante.toString()] || 0;
                
                return (
                  <Card key={quadrante} className={`${config.cor} border-2 cursor-pointer hover:shadow-md transition-shadow`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <IconComponent className="h-5 w-5" />
                        <span className="text-2xl font-bold">{count}</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{config.nome}</p>
                        <p className="text-xs text-gray-600 mt-1">{config.estrategia}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderMatrizVisual = () => (
    <div className="space-y-6">
      {/* Controles e Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Matriz 9Box Interativa</span>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline">
                <Filter className="h-4 w-4 mr-1" />
                Filtros
              </Button>
              <Button size="sm" variant="outline">
                <Download className="h-4 w-4 mr-1" />
                Exportar
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Eixos da Matriz */}
          <div className="relative">
            {/* Labels dos Eixos */}
            <div className="absolute -left-16 top-1/2 transform -rotate-90 text-sm font-medium text-gray-600">
              POTENCIAL
            </div>
            <div className="absolute bottom-[-40px] left-1/2 transform -translate-x-1/2 text-sm font-medium text-gray-600">
              PERFORMANCE
            </div>

            {/* Grid da Matriz 9Box */}
            <div className="grid grid-cols-3 gap-2 w-full max-w-3xl mx-auto">
              {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((quadrante) => {
                const config = quadrantesConfig[quadrante];
                const IconComponent = config.icon;
                const colaboradoresQuadrante = visualizacao.quadrantes?.[quadrante] || [];
                
                return (
                  <Card 
                    key={quadrante} 
                    className={`${config.cor} border-2 h-32 cursor-pointer hover:shadow-lg transition-all`}
                    onClick={() => handleQuadranteClick(quadrante, colaboradoresQuadrante)}
                  >
                    <CardContent className="p-3 h-full flex flex-col">
                      <div className="flex items-center justify-between mb-1">
                        <IconComponent className="h-4 w-4" />
                        <span className="text-xs font-bold">Q{quadrante}</span>
                      </div>
                      
                      <div className="mb-2">
                        <p className="font-medium text-xs leading-tight">{config.nome}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {colaboradoresQuadrante.length} colaborador{colaboradoresQuadrante.length !== 1 ? 'es' : ''}
                        </p>
                      </div>
                      
                      {/* Mostrar alguns colaboradores */}
                      <div className="flex-1 overflow-hidden">
                        {colaboradoresQuadrante.slice(0, 2).map((collab, index) => (
                          <div key={index} className="text-xs truncate">
                            {collab.colaborador_nome}
                          </div>
                        ))}
                        {colaboradoresQuadrante.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{colaboradoresQuadrante.length - 2} mais
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Legenda dos Eixos */}
            <div className="mt-8 grid grid-cols-2 gap-8">
              <div>
                <h4 className="font-medium mb-2">Performance (Eixo X)</h4>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Baixa</span>
                  <span>Média</span>
                  <span>Alta</span>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Potencial (Eixo Y)</h4>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Baixo</span>
                  <span>Médio</span>
                  <span>Alto</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detalhes do Colaborador Selecionado */}
      {selectedColaborador && (
        <Card className="border-2 border-blue-500 shadow-xl mt-6">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2 text-xl">
                  <User className="h-6 w-6 text-blue-600" />
                  <span>Detalhes: {selectedColaborador.colaborador_nome}</span>
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedColaborador.colaborador_cargo || 'Cargo não informado'}
                </p>
              </div>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedColaborador(null)}
                >
                  <span className="text-lg">×</span>
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleGerarPlanoSucessao(selectedColaborador.colaborador_id)}
                >
                  <Crown className="h-4 w-4 mr-1" />
                  Plano de Sucessão
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Métricas Principais */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-600">Performance</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedColaborador.score_performance?.toFixed(1) || 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Rocket className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-600">Potencial</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {selectedColaborador.score_potencial?.toFixed(1) || 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Star className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-600">Quadrante</p>
                      <p className="text-2xl font-bold text-green-600">
                        Q{selectedColaborador.quadrante || 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Award className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="text-xs text-gray-600">Classificação</p>
                      <p className="text-sm font-bold text-orange-600">
                        {selectedColaborador.classificacao || 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Estratégia e Insights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pontos Fortes */}
              {selectedColaborador.pontos_fortes && selectedColaborador.pontos_fortes.length > 0 && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                      <ThumbsUp className="h-4 w-4 mr-2" />
                      Pontos Fortes
                    </h4>
                    <ul className="space-y-2">
                      {selectedColaborador.pontos_fortes.map((ponto, idx) => (
                        <li key={idx} className="text-sm text-green-700 flex items-start">
                          <span className="mr-2">•</span>
                          <span>{ponto}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              
              {/* Áreas de Desenvolvimento */}
              {selectedColaborador.areas_desenvolvimento && selectedColaborador.areas_desenvolvimento.length > 0 && (
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-orange-800 mb-3 flex items-center">
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Áreas de Desenvolvimento
                    </h4>
                    <ul className="space-y-2">
                      {selectedColaborador.areas_desenvolvimento.map((area, idx) => (
                        <li key={idx} className="text-sm text-orange-700 flex items-start">
                          <span className="mr-2">•</span>
                          <span>{area}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Estratégia Sugerida */}
            {selectedColaborador.estrategia_sugerida && (
              <Card className="mt-6 bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                    <Brain className="h-4 w-4 mr-2" />
                    Estratégia Sugerida (IA)
                  </h4>
                  <p className="text-sm text-blue-700">
                    {selectedColaborador.estrategia_sugerida}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Ações Recomendadas */}
            {selectedColaborador.acoes_recomendadas && selectedColaborador.acoes_recomendadas.length > 0 && (
              <Card className="mt-6 bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Ações Recomendadas
                  </h4>
                  <ul className="space-y-2">
                    {selectedColaborador.acoes_recomendadas.map((acao, idx) => (
                      <li key={idx} className="text-sm text-purple-700 flex items-start">
                        <span className="mr-2">{idx + 1}.</span>
                        <span>{acao}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderConfiguracoes = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Criar Nova Matriz 9Box</CardTitle>
          <CardDescription>
            Configure uma nova matriz para mapeamento de talentos com IA integrada
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateMatriz} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome da Matriz</Label>
                <Input
                  id="nome"
                  value={novaMatriz.nome}
                  onChange={(e) => setNovaMatriz(prev => ({...prev, nome: e.target.value}))}
                  placeholder="Ex: 9Box 2025 - Mapeamento de Talentos"
                  required
                />
              </div>

              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Input
                  id="descricao"
                  value={novaMatriz.descricao}
                  onChange={(e) => setNovaMatriz(prev => ({...prev, descricao: e.target.value}))}
                  placeholder="Descrição da matriz"
                />
              </div>

              <div>
                <Label htmlFor="periodo_inicio">Período de Início</Label>
                <Input
                  id="periodo_inicio"
                  type="date"
                  value={novaMatriz.periodo_inicio}
                  onChange={(e) => {
                    const val = e.target.value;
                    const year = val.split('-')[0];
                    if (year && year.length > 4) return;
                    
                    setNovaMatriz(prev => {
                      const newState = {...prev, periodo_inicio: val};
                      // Se a nova data de início for maior que a data de fim já preenchida, limpa o fim
                      if (newState.periodo_fim && val > newState.periodo_fim) {
                        newState.periodo_fim = '';
                      }
                      return newState;
                    });
                  }}
                  required
                />
              </div>

              <div>
                <Label htmlFor="periodo_fim">Período de Fim</Label>
                <Input
                  id="periodo_fim"
                  type="date"
                  min={novaMatriz.periodo_inicio || undefined}
                  value={novaMatriz.periodo_fim}
                  min={novaMatriz.periodo_inicio}
                  onChange={(e) => {
                    const val = e.target.value;
                    const year = val.split('-')[0];
                    if (year && year.length > 4) return;
                    
                    if (novaMatriz.periodo_inicio && val < novaMatriz.periodo_inicio) {
                      toast.error('A data final não pode ser anterior à data inicial');
                      return;
                    }
                    
                    setNovaMatriz(prev => ({...prev, periodo_fim: val}));
                  }}
                  required
                />
              </div>
            </div>

            {/* Configuração de Pesos */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium">Configuração de Cálculo</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Peso Performance (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={novaMatriz.peso_performance}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNovaMatriz(prev => ({
                        ...prev, 
                        peso_performance: val === '' ? '' : Number(val),
                        peso_potencial: val === '' ? 100 : 100 - Number(val)
                      }));
                    }}
                    onBlur={() => {
                      if (novaMatriz.peso_performance === '') {
                        setNovaMatriz(prev => ({
                          ...prev,
                          peso_performance: 0,
                          peso_potencial: 100
                        }));
                      }
                    }}
                  />
                </div>
                
                <div>
                  <Label>Peso Potencial (%)</Label>
                  <Input
                    type="number"
                    value={novaMatriz.peso_potencial}
                    disabled
                  />
                </div>

                <div>
                  <Label>Fonte de Performance</Label>
                  <Select 
                    value={novaMatriz.fonte_performance}
                    onValueChange={(value) => setNovaMatriz(prev => ({...prev, fonte_performance: value}))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="avaliacao_disc">Avaliação DISC</SelectItem>
                      <SelectItem value="avaliacao_desempenho">Avaliação de Desempenho</SelectItem>
                      <SelectItem value="fit_cultural">Fit Cultural</SelectItem>
                      <SelectItem value="feedback_90">Feedback 90º</SelectItem>
                      <SelectItem value="feedback_180">Feedback 180º</SelectItem>
                      <SelectItem value="feedback_360">Feedback 360º</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Fonte de Potencial</Label>
                  <Select 
                    value={novaMatriz.fonte_potencial}
                    onValueChange={(value) => setNovaMatriz(prev => ({...prev, fonte_potencial: value}))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="avaliacao_disc">Avaliação DISC</SelectItem>
                      <SelectItem value="avaliacao_desempenho">Avaliação de Desempenho</SelectItem>
                      <SelectItem value="fit_cultural">Fit Cultural</SelectItem>
                      <SelectItem value="feedback_90">Feedback 90º</SelectItem>
                      <SelectItem value="feedback_180">Feedback 180º</SelectItem>
                      <SelectItem value="feedback_360">Feedback 360º</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Configurações Avançadas */}
              <div className="space-y-3 pt-4 border-t">
                <h5 className="font-medium text-sm">Configurações Avançadas</h5>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="permitir_ajuste_manual"
                    checked={novaMatriz.permitir_ajuste_manual}
                    onChange={(e) => setNovaMatriz(prev => ({...prev, permitir_ajuste_manual: e.target.checked}))}
                  />
                  <Label htmlFor="permitir_ajuste_manual" className="text-sm">
                    Permitir ajustes manuais pós-calibração
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="requer_calibracao"
                    checked={novaMatriz.requer_calibracao}
                    onChange={(e) => setNovaMatriz(prev => ({...prev, requer_calibracao: e.target.checked}))}
                  />
                  <Label htmlFor="requer_calibracao" className="text-sm">
                    Requerer sessão de calibração
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="gerar_pdi_automatico"
                    checked={novaMatriz.gerar_pdi_automatico}
                    onChange={(e) => setNovaMatriz(prev => ({...prev, gerar_pdi_automatico: e.target.checked}))}
                  />
                  <Label htmlFor="gerar_pdi_automatico" className="text-sm">
                    Gerar PDIs automaticamente
                  </Label>
                </div>
              </div>
            </div>

            <Button type="submit" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Matriz'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  const renderSidebar = () => {
    if (!sidebarOpen || !selectedQuadrante) return null;
    
    const config = quadrantesConfig[selectedQuadrante.numero];
    const IconComponent = config.icon;
    
    return (
      <>
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={closeSidebar}
        />
        
        {/* Sidebar */}
        <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className={`p-3 rounded-lg ${config.cor}`}>
                  <IconComponent className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Q{selectedQuadrante.numero}</h3>
                  <p className="text-sm text-gray-600">{config.nome}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={closeSidebar}
              >
                <span className="text-xl">×</span>
              </Button>
            </div>
            
            {/* Estatísticas */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total de Colaboradores</p>
                  <p className="text-2xl font-bold">{selectedQuadrante.colaboradores.length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500">{config.estrategia}</p>
              </div>
            </div>
            
            {/* Lista de Colaboradores */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-700 mb-3">Colaboradores</h4>
              
              {selectedQuadrante.colaboradores.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum colaborador neste quadrante</p>
                </div>
              ) : (
                selectedQuadrante.colaboradores.map((collab, index) => (
                  <Card 
                    key={index}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      console.log('Colaborador selecionado:', collab);
                      setSelectedColaborador(collab);
                      closeSidebar();
                      // Scroll para o topo para ver o painel de detalhes
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{collab.colaborador_nome}</p>
                          <p className="text-xs text-gray-500 mt-1">{collab.colaborador_cargo || 'Cargo não informado'}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                      </div>
                      
                      {/* Scores */}
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="flex items-center space-x-2">
                          <Target className="h-3 w-3 text-blue-500" />
                          <div>
                            <p className="text-xs text-gray-500">Performance</p>
                            <p className="text-sm font-semibold">{collab.score_performance?.toFixed(1) || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Rocket className="h-3 w-3 text-purple-500" />
                          <div>
                            <p className="text-xs text-gray-500">Potencial</p>
                            <p className="text-sm font-semibold">{collab.score_potencial?.toFixed(1) || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Classificação */}
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <Badge className="text-xs">{collab.classificacao}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
            
            {/* Ações */}
            {selectedQuadrante.colaboradores.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <Button className="w-full" variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar Lista
                </Button>
                <Button className="w-full" variant="outline" size="sm">
                  <Brain className="h-4 w-4 mr-2" />
                  Análise com IA
                </Button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center space-x-2">
          <Diamond className="h-8 w-8 text-purple-600" />
          <span>9Box Inteligente</span>
        </h1>
        <p className="text-gray-600 mt-2">
          Matriz inteligente para mapeamento de talentos com análise preditiva de IA e geração automática de planos de sucessão
        </p>
      </div>
      
      {/* Renderizar Sidebar */}
      {renderSidebar()}

      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="colaboradores">Colaboradores</TabsTrigger>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="matriz">Matriz Visual</TabsTrigger>
          <TabsTrigger value="sucessao">Sucessão</TabsTrigger>
          <TabsTrigger value="calibracao">Calibração</TabsTrigger>
          <TabsTrigger value="configuracoes">Nova Matriz 9Box</TabsTrigger>
        </TabsList>

        <TabsContent value="colaboradores">
          {renderColaboradores()}
        </TabsContent>

        <TabsContent value="dashboard">
          {renderDashboard()}
        </TabsContent>

        <TabsContent value="matriz">
          {renderMatrizVisual()}
        </TabsContent>

        <TabsContent value="sucessao">
          <Suspense fallback={<div className="text-center py-12">Carregando...</div>}>
            <SucessaoTab matrizAtiva={matrizAtiva} />
          </Suspense>
        </TabsContent>

        <TabsContent value="calibracao">
          <Suspense fallback={<div className="text-center py-12">Carregando...</div>}>
            <CalibracaoTab matrizAtiva={matrizAtiva} visualizacao={visualizacao} />
          </Suspense>
        </TabsContent>

        <TabsContent value="configuracoes">
          {renderConfiguracoes()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NineBoxInteligente;